import { FC, ReactNode } from "react";
import { Category } from "@common/types/category";
import { Entity } from "@common/types/entity";
import { StandardModal } from "@design-system/Modal/Modal";
import { SelectBusinessEntityModal } from "@components/entity/SelectBusinessEntityModal";
import { CreateCounterpartyModal } from "@components/counterparty/CreateCounterpartyModal";
import { CounterpartyCreationResponse } from "@common/contracts/counterparty.contract";
import { SelectedTransaction } from "./transactionsBulkActionsContext";
import { NoteAction } from "./NoteDropdown/NoteAction";
import { UnmatchAllConfirmationModal } from "./TransactionsActionBar/UnmatchAllConfirmationModal";
import { UnsplitAllConfirmationModal } from "./TransactionsActionBar/UnsplitAllConfirmationModal";
import { RevertGeneralLedgerConfirmationModal } from "./TransactionsActionBar/RevertGeneralLedgerConfirmationModal";
import { RemoveCounterpartyConfirmationModal } from "./TransactionsActionBar/RemoveCounterpartyConfirmationModal";
import { RemoveCustomerConfirmationModal } from "./TransactionsActionBar/RemoveCustomerConfirmationModal";
import { BulkDeleteConfirmationModal } from "./TransactionsActionBar/BulkDeleteConfirmationModal";
import { ReconcileGustoTransfersModal } from "./ReconcileGustoTransfer/ReconcileGustoTransfersModal";

interface TransactionsBulkModalsProps {
    shouldShowUnmatch: boolean;
    closeUnmatch: () => void;
    shouldShowUnsplit: boolean;
    closeUnsplit: () => void;
    shouldShowRevertGL: boolean;
    closeRevertGL: () => void;
    confirmRevertGL: () => Promise<void>;
    shouldShowRemoveCounterparty: boolean;
    closeRemoveCounterparty: () => void;
    confirmRemoveCounterparty: () => Promise<void>;
    shouldShowRemoveCustomer: boolean;
    closeRemoveCustomer: () => void;
    confirmRemoveCustomer: () => Promise<void>;
    shouldShowDelete: boolean;
    closeDelete: () => void;
    confirmDelete: () => Promise<void>;
    shouldShowGusto: boolean;
    closeGusto: () => void;
    selectedArray: SelectedTransaction[];
    shouldShowCreateCounterparty: boolean;
    closeCreateCounterparty: () => void;
    onCreateCounterpartyClosed: (
        response?: CounterpartyCreationResponse,
    ) => void;
    shouldShowCreateCustomer: boolean;
    closeCreateCustomer: () => void;
    onCreateCustomerClosed: (response?: CounterpartyCreationResponse) => void;
    shouldShowMemo: boolean;
    closeMemo: () => void;
    switchingToBusinessCategory: Category | null;
    hideBusinessSelection: () => void;
    onBusinessSelected: (entity: Entity) => void;
    scheduleConfirmModal?: ReactNode;
}

export const TransactionsBulkModals: FC<TransactionsBulkModalsProps> = ({
    shouldShowUnmatch,
    closeUnmatch,
    shouldShowUnsplit,
    closeUnsplit,
    shouldShowRevertGL,
    closeRevertGL,
    confirmRevertGL,
    shouldShowRemoveCounterparty,
    closeRemoveCounterparty,
    confirmRemoveCounterparty,
    shouldShowRemoveCustomer,
    closeRemoveCustomer,
    confirmRemoveCustomer,
    shouldShowDelete,
    closeDelete,
    confirmDelete,
    shouldShowGusto,
    closeGusto,
    selectedArray,
    shouldShowCreateCounterparty,
    closeCreateCounterparty,
    onCreateCounterpartyClosed,
    shouldShowCreateCustomer,
    closeCreateCustomer,
    onCreateCustomerClosed,
    shouldShowMemo,
    closeMemo,
    switchingToBusinessCategory,
    hideBusinessSelection,
    onBusinessSelected,
    scheduleConfirmModal,
}) => (
    <>
        <UnmatchAllConfirmationModal
            show={shouldShowUnmatch}
            onHide={closeUnmatch}
        />

        <UnsplitAllConfirmationModal
            show={shouldShowUnsplit}
            onHide={closeUnsplit}
        />

        <RevertGeneralLedgerConfirmationModal
            show={shouldShowRevertGL}
            onHide={closeRevertGL}
            onConfirm={confirmRevertGL}
        />

        <RemoveCounterpartyConfirmationModal
            show={shouldShowRemoveCounterparty}
            onHide={closeRemoveCounterparty}
            onConfirm={confirmRemoveCounterparty}
        />

        <RemoveCustomerConfirmationModal
            show={shouldShowRemoveCustomer}
            onHide={closeRemoveCustomer}
            onConfirm={confirmRemoveCustomer}
        />

        <BulkDeleteConfirmationModal
            show={shouldShowDelete}
            onHide={closeDelete}
            onConfirm={confirmDelete}
        />

        <ReconcileGustoTransfersModal
            show={shouldShowGusto}
            onClose={closeGusto}
            selectedTransactions={selectedArray}
        />

        <StandardModal
            show={shouldShowCreateCounterparty}
            onHide={closeCreateCounterparty}
            size="lg"
        >
            <CreateCounterpartyModal
                close={onCreateCounterpartyClosed}
                forTransactionEditing
            />
        </StandardModal>

        <StandardModal
            show={shouldShowCreateCustomer}
            onHide={closeCreateCustomer}
            size="lg"
        >
            <CreateCounterpartyModal
                close={onCreateCustomerClosed}
                forTransactionEditing
            />
        </StandardModal>

        <StandardModal show={shouldShowMemo} onHide={closeMemo}>
            <NoteAction
                inputPlaceholder="Add memo..."
                onSubmitCallback={closeMemo}
            />
        </StandardModal>

        <StandardModal
            show={!!switchingToBusinessCategory}
            onHide={hideBusinessSelection}
        >
            <SelectBusinessEntityModal
                prompt="To assign a business category please select a business entity you'd like to move this transaction to:"
                onCancel={hideBusinessSelection}
                onSelected={onBusinessSelected}
                selectPlaceholder="Select entity"
            />
        </StandardModal>
        {scheduleConfirmModal}
    </>
);
